import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, ArrowRight, Droplets, Beaker, TrendingUp, ArrowDown, ExternalLink, AlertTriangle, Lightbulb, Target, Zap } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEOHead from '../components/SEOHead';
import CropCTASection from '../components/CropCTASection';

const RaspberryPage = () => {

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title="Raspberry Success: The Critical pH Factor"
        description="Research-backed pH management to maximize your raspberry profits. Learn how water quality affects your raspberry crop yield and quality."
        canonicalPath="/raspberry"
      />
      {/* Navbar */}
      <Navbar />

      {/* Hero Section */}
      <section className="relative min-h-screen md:min-h-[70vh] flex items-center justify-center overflow-hidden pt-16 pb-8">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src="/Raspberries.jpg"
            alt="Fresh raspberries on the vine"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-red-900/80 via-rose-800/70 to-black/60"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center py-8">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-white"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium mb-4 md:mb-6"
            >
              <span className="text-xl">🍓</span>
              <span>High-Value Berry Resource</span>
            </motion.div>

            <h1 className="text-4xl md:text-5xl lg:text-7xl font-bold mb-4 md:mb-6 leading-tight">
              Raspberry Success:
              <span className="block text-rose-300">The Critical pH Factor</span>
            </h1>
            
            <p className="text-lg md:text-xl lg:text-2xl mb-6 md:mb-8 text-white/90 leading-relaxed max-w-4xl mx-auto">
              Research-backed pH management to maximize your profits
            </p>

            <motion.button
              onClick={() => document.getElementById('intro-content')?.scrollIntoView({ behavior: 'smooth' })}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="bg-rose-500 text-white px-8 md:px-12 py-3 md:py-4 rounded-lg font-bold text-lg md:text-xl hover:bg-rose-600 transition-colors inline-flex items-center space-x-3 shadow-lg mb-6 md:mb-8"
            >
              <span>Start Reading Guide</span>
              <ArrowDown size={20} className="md:w-6 md:h-6" />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Intro Content */}
      <section id="intro-content" className="py-16 md:py-20 bg-gradient-to-br from-rose-50 to-red-50">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-rose-100 text-rose-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <span className="text-xl">🍓</span>
              <span>Introduction</span>
            </div>
            
            <div className="bg-white rounded-2xl p-6 md:p-8 shadow-lg border border-rose-200">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                <strong>Raspberries thrive in acidic soil conditions</strong> — and your irrigation water could be quietly pushing your soil pH up season after season, affecting cane growth, berry size, and overall yield.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed mt-4">
                At CTC Waterworks, we've helped berry growers tackle hidden irrigation problems for 40+ years. This guide explains why your water pH matters for raspberries, what research says, and how a simple sulfur burner can help protect your soil, your yield, and your bottom line.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Research-Based Content Sections */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-16">
          
          {/* Section 1: Ideal Soil pH for Raspberries */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🔍</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                1: Ideal Soil pH for Raspberries
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-xl p-6 md:p-8 border border-rose-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-rose-500 pl-6">
                "Raspberries thrive in acidic soils with an optimal pH range of 5.5–6.5. When soil pH creeps above this range, nutrient availability drops, resulting in poor cane growth and lower yields."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-rose-600" size={16} />
                <span className="text-sm font-medium text-rose-600">
                  Source: Oregon State University Extension — Raspberry Cultivation in Oregon
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Target className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">If your water is high in bicarbonates, it can gradually push your soil pH too high — tying up key nutrients like iron and manganese and causing yellowing or stunted canes.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 2: Hidden Risk - Bicarbonates in Irrigation Water */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">💧</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                2: Hidden Risk — Bicarbonates in Irrigation Water
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-xl p-6 md:p-8 border border-orange-200">
              <p className="text-lg md:text-xl text-gray-700 mb-6 leading-relaxed">
                Think of bicarbonates as tiny pH "buffers" floating in your water. When you irrigate with high-bicarbonate water, 
                these compounds slowly but steadily neutralize the acidity in your soil. It's like adding baking soda to your 
                raspberry beds, drop by drop, season after season. Even if your soil pH starts perfect, alkaline irrigation 
                water can gradually push it out of the ideal range.
              </p>

              <blockquote className="text-lg text-gray-700 mb-6 italic border-l-4 border-orange-500 pl-6 bg-white rounded-lg p-4">
                "High bicarbonate irrigation water raises soil pH over time. In acid-loving berry crops like raspberries and blueberries, this can limit nutrient uptake and reduce plant vigor."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-orange-600" size={16} />
                <span className="text-sm font-medium text-orange-600">
                  Source: University of Maine Extension — Wild Blueberry Fact Sheet — Irrigation & Water Quality
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Raspberry growers face the same creeping pH risk as blueberries — test your water's bicarbonates annually to avoid unexpected soil drift.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 3: Impact on Drip & Microirrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🚜</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                3: Impact on Drip & Microirrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-blue-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-blue-500 pl-6">
                "Carbonate and bicarbonate salts in hard water can precipitate inside drip systems, causing partial or complete plugging of emitters."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-blue-600" size={16} />
                <span className="text-sm font-medium text-blue-600">
                  Source: UC ANR — Microirrigation of Trees and Vines
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Zap className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Clogged emitters mean patchy watering, uneven berry ripening, and more disease risk. Inline acidification helps dissolve these salts before they plug your lines.</p>
              </div>

              {/* Problem vs Solution Visual */}
              <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                  <h4 className="font-semibold text-red-800 mb-3">❌ High-Bicarbonate Water Problems:</h4>
                  <ul className="space-y-2 text-sm text-red-700">
                    <li>• Clogged drip emitters</li>
                    <li>• Uneven water distribution</li>
                    <li>• Patchy berry ripening</li>
                    <li>• Increased disease risk</li>
                  </ul>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <h4 className="font-semibold text-green-800 mb-3">✅ With pH Management:</h4>
                  <ul className="space-y-2 text-sm text-green-700">
                    <li>• Clean, efficient emitters</li>
                    <li>• Uniform water application</li>
                    <li>• Even berry ripening</li>
                    <li>• Reduced disease pressure</li>
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Section 4: PNW-Specific Insight - Berry Irrigation */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">🌲</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                4: PNW-Specific Insight — Berry Irrigation
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-xl p-6 md:p-8 border border-green-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-green-500 pl-6">
                "Berry crops in the Pacific Northwest require careful water quality monitoring to manage soil pH and maintain nutrient availability."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-green-600" size={16} />
                <span className="text-sm font-medium text-green-600">
                  Source: WSU Extension — Irrigation Water Quality for Berry Crops
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500">
                <div className="flex items-center space-x-2 mb-2">
                  <Lightbulb className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">Don't assume your water is safe because your soil was acidic when you planted. Bicarbonates add up — a sulfur burner keeps your irrigation water pH right where your raspberries want it.</p>
              </div>
            </div>
          </motion.div>

          {/* Section 5: Bicarbonate Threshold */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-lg flex items-center justify-center">
                <span className="text-white text-xl">📊</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900">
                5: Bicarbonate Threshold
              </h2>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-xl p-6 md:p-8 border border-purple-200">
              <blockquote className="text-lg md:text-xl text-gray-700 mb-6 italic border-l-4 border-purple-500 pl-6">
                "When irrigation water bicarbonates exceed 120 ppm, it is recommended to use acidification to protect soil pH and prevent infiltration decline."
              </blockquote>
              
              <div className="flex items-center space-x-2 mb-4">
                <ExternalLink className="text-purple-600" size={16} />
                <span className="text-sm font-medium text-purple-600">
                  Source: UCANR — Salinity & Drainage in Agriculture (FAO Soils Bulletin)
                </span>
              </div>
              
              <div className="bg-white rounded-lg p-4 border-l-4 border-green-500 mb-6">
                <div className="flex items-center space-x-2 mb-2">
                  <CheckCircle className="text-green-600" size={20} />
                  <span className="font-semibold text-green-800">What This Means for You:</span>
                </div>
                <p className="text-gray-700">When you neutralize bicarbonates inline, you protect your soil's natural acidity, keep canes healthy, and grow better berries year after year.</p>
              </div>
              
              <div className="bg-white rounded-lg p-6">
                <h4 className="text-xl font-semibold text-gray-900 mb-4">Why Choose CTC Sulfur Burners for Raspberries:</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {[
                    'Supports root-zone pH stability by neutralizing bicarbonates in irrigation water',
                    'Prevents emitter clogging in drip systems',
                    'Safer than handling liquid acids',
                    'Provides sulfur nutrition as a bonus',
                    'Automated operation with minimal maintenance',
                    '40+ years of proven results in berry operations'
                  ].map((benefit, index) => (
                    <div key={index} className="flex items-center space-x-2">
                      <CheckCircle className="text-green-500 flex-shrink-0" size={16} />
                      <span className="text-gray-700 text-sm">{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Call to Action */}
      <CropCTASection cropName="Raspberries" />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default RaspberryPage;